import type { GlobalConstraintId, MonospaceSingleConstraintField } from '#layers/data-model';
import type { ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/types/schema/resolved-schema';
import type { ExtendApi } from '~/types/constants/schema-overrides';
import { useStore } from '#imports';
import { callOrReturn } from '#layers/base';
import { PROJECT_COLLECTIONS } from '~/constants/collections';
import { useProjectSchemaStore } from '~/stores/project-schema';

export interface ExtendApiDeps {
	findCollectionByName: (name: string) => ResolvedMonospaceCollection | undefined;
	findPrimitiveFieldByName: (collection: ResolvedMonospaceCollection, name: string) => ResolvedMonospacePrimitiveField | undefined;
	getConstraintFieldKey: (mapping: any) => string;
}

export function createExtendApi(
	collection: ResolvedMonospaceCollection,
	state: Record<string, Record<string, any>>,
	deps: ExtendApiDeps,
): ExtendApi<any> {
	return {
		addPrimitiveField(field) {
			const collectionState = state['MonospacePrimitiveField'] ??= {};

			collectionState[field.id] = { ...field, isStudioOnly: true };

			if (field.meta) {
				const collectionStateMeta = state['MonospacePrimitiveFieldMeta'] ??= {};
				collectionStateMeta[field.id] = { ...field.meta, fieldId: field.id };
			}

			return field.id;
		},
		addSingleRelationField(fieldName, toCollectionName, options) {
			const fromCollectionId = collection.id;
			const fieldId = options.id;
			const constraintId = crypto.randomUUID() as GlobalConstraintId;

			const toCollection = deps.findCollectionByName(toCollectionName);

			if (!toCollection) {
				console.error(`Could not find collection with name ${toCollectionName}`);
				return;
			}

			const singleRelationFieldState = state['MonospaceSingleRelationField'] ??= {};

			singleRelationFieldState[fieldId] = {
				id: fieldId,
				collectionId: fromCollectionId,
				oppositeCollectionId: toCollection.id,
				apiName: fieldName,
				isList: options.isList ?? false,
				constraintId: options.fields ? constraintId : undefined,
				isStudioOnly: true,
			};

			if (options.meta) {
				const singleRelationFieldMetaState = state['MonospaceSingleRelationFieldMeta'] ??= {};
				singleRelationFieldMetaState[fieldId] = { ...options.meta, fieldId };
			}

			if (options.fields) {
				const singleConstraintState = state['MonospaceSingleConstraint'] ??= {};
				const singleConstraintFieldState = state['MonospaceSingleConstraintField'] ??= {};

				singleConstraintState[constraintId] = {
					id: constraintId,
				};

				for (const [order, fromFieldPath, toFieldPath] of Object.entries(options.fields).map((fields, index): [number, string, string] => [index, ...fields as [string, string]])) {
					const fromFieldName = fromFieldPath.split('.')[1]!;
					const toFieldName = toFieldPath.split('.')[1]!;
					const fromField = deps.findPrimitiveFieldByName(collection, fromFieldName);
					const toField = deps.findPrimitiveFieldByName(toCollection, toFieldName);

					if (!fromField) {
						console.error(`Could not find field ${fromFieldName} in collection ${collection.apiName}`);
						continue;
					}

					if (!toField) {
						console.error(`Could not find field ${toFieldName} in collection ${toCollection.apiName}`);
						continue;
					}

					const fieldMapping: MonospaceSingleConstraintField = {
						order,
						constraintId,
						constrainedFieldId: fromField.id,
						referencedFieldId: toField.id,
					};

					const stateKey = deps.getConstraintFieldKey(fieldMapping);
					singleConstraintFieldState[stateKey] = fieldMapping;
				}
			}

			return fieldId;
		},
	};
}

const layerIds = new Set<string>();

export async function applySchemaOverrides() {
	const store = useStore();
	const { findCollectionByName, findPrimitiveFieldByName, findSingleRelationFieldByName } = useProjectSchemaStore();

	const state: Record<string, Record<string, any>> = {};

	// Remove old layers before field lookups, otherwise overrides from the previous run
	// (e.g. apiName: '') can cause fields to not be found by their original name.
	for (const layerId of layerIds) {
		store.$cache.removeLayer(layerId);
	}

	layerIds.clear();

	for (const [collectionKey, config] of Object.entries(PROJECT_COLLECTIONS)) {
		if (!config.schema) continue;

		const collection = findCollectionByName(collectionKey);

		if (!collection) {
			console.warn(`Could not find collection ${collectionKey}`);
			continue;
		}

		const overrides = config.schema;

		const { $extend, $meta, ...fieldOverrides } = callOrReturn(overrides, collection);

		if ($meta) {
			state['MonospaceCollectionMeta'] ??= {};
			state['MonospaceCollectionMeta'][collection.id] = { ...$meta, collectionId: collection.id };
		}

		for (const fieldKey in fieldOverrides) {
			const overrides = (fieldOverrides as Record<string, any>)[fieldKey];
			const primitiveField = findPrimitiveFieldByName(collection, fieldKey);
			const relationField = findSingleRelationFieldByName(collection, fieldKey);

			const field: any = primitiveField ?? relationField;

			if (field) {
				const { apiName: _, ...fieldOverrides } = callOrReturn(overrides, field);

				const collectionState = state[field.$collection] ??= {};
				collectionState[field.$getKey()] = { ...fieldOverrides };

				if (fieldOverrides.meta) {
					const collectionStateMeta = state[`${field.$collection}Meta`] ??= {};
					collectionStateMeta[field.$getKey()] = { ...fieldOverrides.meta, fieldId: field.id };
				}
			}
			else {
				console.warn(`Could not find field ${fieldKey} in collection ${collectionKey}. Available fields ${collection.primitiveFields.map((field) => field.apiName).join(', ') || 'none'}`);
			}
		}

		if ($extend) {
			applyExtend(collection, $extend, state);
		}
	}

	for (const collectionName in state) {
		const id = `schema-overrides:${collectionName}`;
		layerIds.add(id);

		store.$cache.addLayer({
			id,
			collectionName,
			state: state[collectionName]!,
			deletedItems: new Set(),
		});
	}

	function applyExtend(collection: ResolvedMonospaceCollection, extendHandler: (api: ExtendApi<any>) => void, state: Record<string, any>) {
		const api = createExtendApi(collection, state, { findCollectionByName, findPrimitiveFieldByName, getConstraintFieldKey: (mapping: any) => String(store.MonospaceSingleConstraintField.getKey(mapping)!) });
		extendHandler(api);
	}
}
